<template>
  <div ref="selectedTeamWrapper">
    <div ref="selectedTeam" :style="`width: ${pitchImage.width || pitchImage.originWidth}px;`">
      <div class="team-selector-header" :style="sizeStyle(headerImage)"/>
      <div class="team-selector">
        <div class="team-selector-field" :style="sizeStyle(pitchImage)">
          <div class="team-selector-logo" :style="sizeStyle(logoImage)" />
          <div class="team-selector-url"  :style="sizeStyle(urlImage)" />
          <template v-for="player in playersInFormation">
            <team-player
              :x="player.x"
              :y="player.y"
              :key="player.teamPosition"
              :player="selectedPlayers[player.teamPosition]"
              @click.native="showTeamPlayerSelector(player.teamPosition)"
            />
          </template>
        </div>
        <div class="team-selector-footer text-center pb-3" data-html2canvas-ignore>
          <button
            type="button"
            class="btn primary"
            @click="showTeamFormationSelector"
            data-html2canvas-ignore
          >Choose Formation</button>
          <div>
            <button
              type="button"
              :disabled="!shareEnabled"
              class="btn primary"
              @click="shareTeamSelection"
              data-html2canvas-ignore
            >Complete/Share</button>
            <button
              type="button"
              :disabled="!shareEnabled"
              class="btn primary"
              @click="downloadTeamSelection"
              data-html2canvas-ignore
            >Download Image</button>
          </div>
        </div>
        <team-player-selector
          v-show="isTeamPlayerSelectorVisible"
          :players="availablePlayers"
          @close="closeTeamPlayerSelector"
          @selectedTeamPlayer="selectedTeamPlayer"
          :teamPosition="clickedTeamPosition"
          data-html2canvas-ignore
        />
        <team-formation-selector
          v-show="isTeamFormationSelectorVisible"
          :formations="formations"
          :formation="selectedFormation"
          @close="closeTeamFormationSelector"
          @selectedTeamFormation="selectedTeamFormation"
          data-html2canvas-ignore
        />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { defaultFormations, defaultPlayers } from './defaults'
import TeamPlayer from './TeamPlayer'
import TeamPlayerSelector from './TeamPlayerSelector'
import TeamFormationSelector from './TeamFormationSelector'
import VueHtml2Canvas from 'vue-html2canvas'
import { spinnakerApi } from '../../js/SpinnakerApi'

Vue.use(VueHtml2Canvas)

export default {
  name: 'TeamSelector',
  components: {
    'team-formation-selector': TeamFormationSelector,
    'team-player-selector': TeamPlayerSelector,
    'team-player': TeamPlayer
  },
  props: {
    formations: {
      type: Array,
      default () {
        return defaultFormations
      }
    },
    players: {
      type: Array,
      default () {
        return defaultPlayers.map((x, index) => Object.assign({ id: index }, x))
      }
    }
  },
  data: function () {
    return {
      isTeamPlayerSelectorVisible: false,
      isTeamFormationSelectorVisible: false,
      selectedPlayers: [],
      clickedTeamPosition: 0,
      selectedFormation: '4-3-3',
      availablePlayers: this.players,
      shareEnabled: false,
      sharedImageData: null,
      sharedImageOpenGraph: null,
      xGapPerPlayer: 20,
      yPlayerOffset: 5,
      selectedTeamImageScale: 1,
      pitchImage: { originWidth: 732, originHeight: 898, width: null, height: null },
      headerImage: { originWidth: 739, originHeight: 197, width: null, height: null },
      logoImage: { originWidth: 99, originHeight: 99, width: null, height: null },
      urlImage: { originWidth: 202, originHeight: 80, width: null, height: null }
    }
  },
  computed: {
    playersInFormation: function () {
      let playersInFormation = []
      let teamPosition = 0
      let formation = this.getFormationFromName(this.selectedFormation)
      let rows = formation.name.split('-')
      let numberOfRows = rows.length + 1

      playersInFormation.push({
        teamPosition: teamPosition,
        x: this.getPlayerXPosition(0, 1),
        y: this.getPlayerYPosition(0, numberOfRows)
      })

      if (formation.positions) {
        playersInFormation = formation.positions.map(function (position, index) {
          return {
            teamPosition: index,
            x: position.x,
            y: position.y
          }
        })
      } else {
        rows.forEach((noInRow, rowNo) => {
          for (let i = 0; i < noInRow; i++) {
            teamPosition++
            playersInFormation.push({
              teamPosition: teamPosition,
              x: this.getPlayerXPosition(i, noInRow),
              y: this.getPlayerYPosition(rowNo + 1, numberOfRows)
            })
          }
        })
      }
      return playersInFormation
    }
  },
  watch: {
    selectedFormation: async function (a, b) {
      if (this.areAllPlayersSelected()) {
        await this.prepareShare()
      }
    }
  },
  methods: {
    areAllPlayersSelected: function () {
      return this.selectedPlayers.filter(player => !!player).length === 11
    },
    getPlayerXPosition (positionInRow, numberInRow) {
      let x =
        (100 - (numberInRow - 1) * this.xGapPerPlayer) / 2 +
        positionInRow * this.xGapPerPlayer
      // console.log(`x ${positionInRow} ${numberInRow} ${x}`)
      return `${x}%`
    },
    getPlayerYPosition (rowNumber, numberOfRows) {
      let y = (95 / numberOfRows) * rowNumber + this.yPlayerOffset
      // console.log(`y ${rowNumber} ${numberOfRows} ${y}`)
      return `${y}%`
    },
    showTeamPlayerSelector (teamPosition) {
      this.clickedTeamPosition = teamPosition
      this.isTeamPlayerSelectorVisible = true
    },
    closeTeamPlayerSelector () {
      this.isTeamPlayerSelectorVisible = false
    },
    async selectedTeamPlayer (teamPosition, playerId) {
      this.closeTeamPlayerSelector()
      this.selectedPlayers[teamPosition] = this.getPlayerFromId(playerId)
      if (this.areAllPlayersSelected()) {
        await this.prepareShare()
        if (this.sharedImageOpenGraph) {
          this.shareEnabled = true
        }
      } else {
        this.shareEnabled = false
      }
      const selectedPlayerIds = this.selectedPlayers.filter(player => !!player).map(player => player.id)
      this.availablePlayers = this.players.filter(player => !selectedPlayerIds.includes(player.id))
    },
    showTeamFormationSelector () {
      this.isTeamFormationSelectorVisible = true
    },
    closeTeamFormationSelector () {
      this.isTeamFormationSelectorVisible = false
    },
    selectedTeamFormation (formation) {
      this.closeTeamFormationSelector()
      this.selectedFormation = formation
    },
    getFormationFromName (name) {
      return this.formations.filter(function (formation) {
        return formation.name === name
      })[0]
    },
    getPlayerFromId (id) {
      return this.players.filter(function (player) {
        return player.id === id
      })[0]
    },
    async prepareShare () {
      this.$store.commit('showLoading')
      try {
        const el = this.$refs.selectedTeam
        // Full list of options: https://html2canvas.hertzen.com/configuration/
        const options = {
          scale: this.selectedTeamImageScale,
          allowTaint: false,
          useCORS: true,
          logging: true
        }
        const canvas = await this.$html2canvas(el, options)
        this.sharedImageData = canvas.toDataURL('image/jpeg', 1.0)
        this.sharedImageOpenGraph = await spinnakerApi.getOpenGraphForBase64Image(this.sharedImageData)
        console.info('openGraph: ', this.sharedImageOpenGraph)
      } catch (exception) {
        console.error('Could not prepare team selector share image', exception)
      } finally {
        this.$store.commit('hideLoading')
      }
    },
    shareTeamSelection () {
      this.$emit('shareTeamSelection', {
        url: this.sharedImageOpenGraph,
        formation: this.selectedFormation,
        players: this.selectedPlayers.map(player => player.number ? `${player.number}. ${player.lastName}` : player.lastName)
      })
    },
    downloadTeamSelection () {
      const a = document.createElement('a')
      a.setAttribute('href', this.sharedImageData)
      if (typeof a.download !== 'undefined') {
        a.setAttribute('download', `AFTV-DreamTeam-${this.selectedFormation}`)
      } else {
        a.setAttribute('target', '_blank')
      }
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    sizeStyle (image) {
      return `width: ${image.width || image.originWidth}px; height: ${image.height || image.originHeight}px`
    },
    onResize (event) {
      const availableWidth = this.$refs['selectedTeamWrapper'].offsetWidth
      this.headerImage.width = availableWidth
      this.headerImage.height = this.headerImage.width / this.headerImage.originWidth * this.headerImage.originHeight

      const pitchScaleRatio = availableWidth / this.pitchImage.originWidth

      this.pitchImage.width = availableWidth
      this.pitchImage.height = this.pitchImage.originHeight * pitchScaleRatio

      this.logoImage.width = this.logoImage.originWidth * pitchScaleRatio
      this.logoImage.height = this.logoImage.originHeight * pitchScaleRatio

      this.urlImage.width = this.urlImage.originWidth * pitchScaleRatio
      this.urlImage.height = this.urlImage.originHeight * pitchScaleRatio

      this.yPlayerOffset = (availableWidth > 730) ? 5 : 7

      const expectedSelectedTeamImageWidth = this.pitchImage.originWidth
      this.selectedTeamImageScale = expectedSelectedTeamImageWidth / availableWidth
    }
  },
  mounted () {
    window.addEventListener('resize', this.onResize)
    this.onResize()
  }
}
</script>
<style>
.team-selector {
  background-image: url("./images/background.jpg");
  background-size: cover;
}

.team-selector-header {
  background-image: url("./images/AFTV Team Selector banner.png");
  background-size: 100%;
  background-repeat: no-repeat;
}

.team-selector-logo {
  background-image: url("./images/AFTV Logo.png");
  background-size: 100%;
  background-repeat: no-repeat;
  position: absolute;
  left: 0;
}

.team-selector-url {
  background-image: url("./images/AFTV Mobi URL.png");
  background-size: 100%;
  background-repeat: no-repeat;
  position: absolute;
  right: 0;
}

.team-selector-row {
}

.team-selector-player {
  width: 16vw;
  text-align: center;
  margin-left: -8vw;
  margin-top: -8vw;
}

img.team-selector-player-image {
  width: 16vw;
  display: block;
}

.sponsor-row,
.sponsor-row img {
  height: 7vh;
}

.team-selector-player-name {
  white-space: nowrap;
  color: white;
  font-size: 0.8rem;
  margin-top: -0.7rem;
}

/* For landscape display */
@media (min-aspect-ratio: 1/1) {
  .team-selector-player {
    width: 14vw;
    text-align: center;
    margin-left: -7vw;
    margin-top: -4vw;
  }

  img.team-selector-player-image {
    width: 14vw;
    display: block;
  }

  .team-selector-player-name {
    margin-top: -0.9rem;
  }
}

.team-selector-field {
  background-image: url("images/Football pitch.png");
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  position: relative;
}

.team-selector-player .select-player {
  color: #A50B03;
  font-size: 1.5rem;
}

.team-selector-player-number,
.team-selector-player-name .first-name,
.team-formation-name {
  display: none;
}

.team-formation-image {
  width: 35vw;
}

.team-player-selector-backdrop,
.team-formation-selector-backdrop {
  z-index: 4;
}

.modal {
  background: #ffffff;
  /*background-image: url("./images/background.jpg");*/
  /*background-size: cover;*/
  background: #ffffff;
  overflow-x: auto;
  display: flex;
  flex-direction: column;
  color: #333333;
  /*text-shadow: 1px 1px #ffffff;*/
}

.team-player-selector-header,
.team-player-selector-footer,
.team-formation-selector-header,
.team-formation-selector-footer {
  background-color: #A50B03;
  color: #ffffff;
}

.team-player-selector-header,
.team-formation-selector-header {
  min-height: 65px;
  text-transform: uppercase;
}

.team-player-selector-body,
.team-formation-selector-body {
  overflow-y: scroll;
  position: relative;
  padding: 10px;
}
</style>
